import React, { useEffect, useMemo, useState } from 'react'
import api from '../lib/api'

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value || 0))
}

function formatNumber(value, options = {}) {
  return new Intl.NumberFormat('pt-BR', options).format(Number(value || 0))
}

function formatDate(value, opts = { year: 'numeric', month: 'short' }) {
  if (!value) return '—'
  return new Intl.DateTimeFormat('pt-BR', opts).format(new Date(value))
}

function StatCard({ title, value, subtitle, accent = '#2563eb' }) {
  return (
    <div style={{ background: '#fff', borderRadius: 16, padding: 20, boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)', borderTop: `4px solid ${accent}` }}>
      <div style={{ color: '#64748b', fontSize: 14, marginBottom: 8 }}>{title}</div>
      <div style={{ color: '#0f172a', fontSize: 30, fontWeight: 800, marginBottom: 6 }}>{value}</div>
      <div style={{ color: '#475569', fontSize: 13 }}>{subtitle}</div>
    </div>
  )
}

function Section({ title, subtitle, children }) {
  return (
    <section style={{ background: '#fff', borderRadius: 20, padding: 24, boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)', marginBottom: 24 }}>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', marginBottom: 6 }}>{title}</h2>
        {subtitle ? <p style={{ color: '#475569' }}>{subtitle}</p> : null}
      </div>
      {children}
    </section>
  )
}

function SimpleTable({ columns, rows }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key} style={{ textAlign: 'left', padding: '12px 10px', borderBottom: '1px solid #e2e8f0', color: '#475569', fontSize: 13, fontWeight: 700 }}>
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length ? rows.map((row, index) => (
            <tr key={row.id || row.year_month || row.user_id || index}>
              {columns.map((column) => (
                <td key={column.key} style={{ padding: '12px 10px', borderBottom: '1px solid #f1f5f9', color: '#0f172a', fontSize: 14, verticalAlign: 'top' }}>
                  {column.render ? column.render(row[column.key], row) : row[column.key] ?? '—'}
                </td>
              ))}
            </tr>
          )) : (
            <tr>
              <td colSpan={columns.length} style={{ padding: 16, color: '#64748b' }}>Nenhum dado encontrado.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

export default function AdminFinancePage({ user }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [overview, setOverview] = useState(null)
  const [monthly, setMonthly] = useState([])
  const [revenueByPlan, setRevenueByPlan] = useState([])
  const [topCustomers, setTopCustomers] = useState([])
  const [reconciliation, setReconciliation] = useState({ pending: null, items: [] })
  const [webhooks, setWebhooks] = useState([])
  const [cohort, setCohort] = useState([])

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true)
        setError('')
        const [overviewRes, monthlyRes, planRes, customersRes, reconciliationRes, webhooksRes, cohortRes] = await Promise.all([
          api.get('/admin/financial/overview'),
          api.get('/admin/financial/monthly-summary?limit=12'),
          api.get('/admin/financial/revenue-by-plan?limit=6'),
          api.get('/admin/financial/top-customers?limit=10'),
          api.get('/admin/financial/reconciliation?limit=10'),
          api.get('/admin/financial/webhooks?limit=10'),
          api.get('/admin/financial/customer-cohort?limit=20')
        ])

        setOverview(overviewRes.data)
        setMonthly(monthlyRes.data.items || [])
        setRevenueByPlan(planRes.data.items || [])
        setTopCustomers(customersRes.data.items || [])
        setReconciliation(reconciliationRes.data || { pending: null, items: [] })
        setWebhooks(webhooksRes.data.items || [])
        setCohort(cohortRes.data.items || [])
      } catch (err) {
        setError(err?.response?.data?.error || 'Falha ao carregar o painel financeiro.')
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [])

  const latest = overview?.current || null
  const deltas = overview?.deltas || null

  const groupedPlans = useMemo(() => {
    return revenueByPlan.reduce((acc, item) => {
      const key = item.year_month || 'sem-mes'
      if (!acc[key]) acc[key] = []
      acc[key].push(item)
      return acc
    }, {})
  }, [revenueByPlan])

  if (loading) {
    return <div style={{ padding: 32, textAlign: 'center' }}>Carregando painel administrativo...</div>
  }

  if (error) {
    return (
      <div style={{ maxWidth: 960, margin: '0 auto' }}>
        <Section title="Painel financeiro do admin" subtitle="Não foi possível carregar os dados executivos.">
          <div style={{ padding: 16, borderRadius: 12, background: '#fee2e2', color: '#991b1b' }}>{error}</div>
        </Section>
      </div>
    )
  }

  return (
    <div style={{ maxWidth: 1280, margin: '0 auto' }}>
      <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1d4ed8 50%, #0ea5e9 100%)', color: '#fff', borderRadius: 24, padding: 28, marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ opacity: 0.9, fontSize: 14, marginBottom: 6 }}>Admin • {user.name}</div>
            <h1 style={{ fontSize: 34, fontWeight: 900, marginBottom: 10 }}>Painel financeiro executivo</h1>
            <p style={{ maxWidth: 720, lineHeight: 1.5, opacity: 0.95 }}>
              Receita, MRR, churn, conciliação Mercado Pago e auditoria financeira em um único lugar, consumindo as views MySQL do projeto.
            </p>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.12)', borderRadius: 18, padding: 16, minWidth: 220 }}>
            <div style={{ fontSize: 13, opacity: 0.9 }}>Competência atual</div>
            <div style={{ fontSize: 28, fontWeight: 800 }}>{latest?.year_month || '—'}</div>
            <div style={{ marginTop: 8, fontSize: 13, opacity: 0.9 }}>MRR atual: {formatCurrency(latest?.mrr)}</div>
            <div style={{ marginTop: 4, fontSize: 13, opacity: 0.9 }}>Receita líquida: {formatCurrency(latest?.net_revenue)}</div>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 24 }}>
        <StatCard title="MRR" value={formatCurrency(latest?.mrr)} subtitle={`Variação: ${formatCurrency(deltas?.mrr)}`} accent="#2563eb" />
        <StatCard title="Receita líquida" value={formatCurrency(latest?.net_revenue)} subtitle={`Variação: ${formatCurrency(deltas?.netRevenue)}`} accent="#059669" />
        <StatCard title="Clientes ativos" value={formatNumber(latest?.active_customers)} subtitle={`Variação: ${formatNumber(deltas?.activeCustomers)}`} accent="#7c3aed" />
        <StatCard title="Churn" value={`${formatNumber(latest?.churn_rate_percent, { maximumFractionDigits: 2 })}%`} subtitle={`Variação: ${formatNumber(deltas?.churnRatePercent, { maximumFractionDigits: 2 })}%`} accent="#dc2626" />
        <StatCard title="ARPU" value={formatCurrency(latest?.arpu)} subtitle={`ARPPU: ${formatCurrency(latest?.arppu)}`} accent="#ea580c" />
        <StatCard title="LTV estimado" value={latest?.estimated_ltv == null ? '—' : formatCurrency(latest?.estimated_ltv)} subtitle={`Aprovação: ${formatNumber(latest?.approval_rate_percent, { maximumFractionDigits: 2 })}%`} accent="#0891b2" />
      </div>

      <Section title="Visão operacional" subtitle="Saúde atual da base e do faturamento acumulado.">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
          <StatCard title="Usuários" value={formatNumber(overview?.users?.totalUsers)} subtitle={`${formatNumber(overview?.users?.students)} alunos • ${formatNumber(overview?.users?.professionals)} profissionais`} accent="#1d4ed8" />
          <StatCard title="Assinaturas ativas" value={formatNumber(overview?.subscriptions?.activeSubscriptionsNow)} subtitle={`${formatNumber(overview?.subscriptions?.pendingSubscriptionsNow)} pendentes`} accent="#16a34a" />
          <StatCard title="Pagamentos aprovados" value={formatNumber(overview?.payments?.approvedPaymentsTotal)} subtitle={`${formatNumber(overview?.payments?.pendingPaymentsTotal)} pendentes`} accent="#f59e0b" />
          <StatCard title="Receita líquida total" value={formatCurrency(overview?.payments?.netRevenueTotal)} subtitle={`${formatNumber(overview?.payments?.totalPayments)} pagamentos no histórico`} accent="#0f766e" />
        </div>
      </Section>

      <Section title="Resumo mensal" subtitle="Últimas competências financeiras calculadas pelas views executivas.">
        <SimpleTable
          columns={[
            { key: 'year_month', label: 'Competência' },
            { key: 'mrr', label: 'MRR', render: (v) => formatCurrency(v) },
            { key: 'net_revenue', label: 'Receita líquida', render: (v) => formatCurrency(v) },
            { key: 'active_customers', label: 'Clientes ativos', render: (v) => formatNumber(v) },
            { key: 'new_subscriptions', label: 'Novas assinaturas', render: (v) => formatNumber(v) },
            { key: 'cancelled_subscriptions', label: 'Cancelamentos', render: (v) => formatNumber(v) },
            { key: 'churn_rate_percent', label: 'Churn', render: (v) => `${formatNumber(v, { maximumFractionDigits: 2 })}%` },
            { key: 'approval_rate_percent', label: 'Aprovação', render: (v) => `${formatNumber(v, { maximumFractionDigits: 2 })}%` }
          ]}
          rows={monthly}
        />
      </Section>

      <Section title="Receita por plano" subtitle="Consolidação por competência e plano faturado.">
        {Object.keys(groupedPlans).length ? Object.entries(groupedPlans).map(([month, items]) => (
          <div key={month} style={{ marginBottom: 20 }}>
            <div style={{ fontWeight: 800, color: '#0f172a', marginBottom: 10 }}>{month}</div>
            <SimpleTable
              columns={[
                { key: 'plan_name', label: 'Plano' },
                { key: 'payments_count', label: 'Pagamentos', render: (v) => formatNumber(v) },
                { key: 'unique_customers', label: 'Clientes', render: (v) => formatNumber(v) },
                { key: 'gross_revenue_approved', label: 'Receita bruta', render: (v) => formatCurrency(v) },
                { key: 'gateway_fees', label: 'Taxas', render: (v) => formatCurrency(v) },
                { key: 'net_revenue', label: 'Receita líquida', render: (v) => formatCurrency(v) }
              ]}
              rows={items}
            />
          </div>
        )) : <div style={{ color: '#64748b' }}>Sem dados por plano.</div>}
      </Section>

      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 24, alignItems: 'start' }}>
        <Section title="Top clientes por valor" subtitle="Maior receita líquida acumulada por cliente.">
          <SimpleTable
            columns={[
              { key: 'name', label: 'Cliente' },
              { key: 'email', label: 'Email' },
              { key: 'approved_payments', label: 'Pag. aprovados', render: (v) => formatNumber(v) },
              { key: 'lifetime_net_revenue', label: 'Receita líquida', render: (v) => formatCurrency(v) },
              { key: 'currently_active', label: 'Ativo', render: (v) => (Number(v) ? 'Sim' : 'Não') }
            ]}
            rows={topCustomers}
          />
        </Section>

        <Section title="Cohort recente" subtitle="Primeira assinatura versus receita em meses subsequentes.">
          <SimpleTable
            columns={[
              { key: 'first_subscription_month', label: 'Cohort', render: (v) => formatDate(v) },
              { key: 'revenue_month', label: 'Mês receita', render: (v) => formatDate(v) },
              { key: 'cohort_age_months', label: 'Idade (meses)', render: (v) => formatNumber(v) },
              { key: 'cohort_customers', label: 'Clientes', render: (v) => formatNumber(v) },
              { key: 'approved_revenue', label: 'Receita', render: (v) => formatCurrency(v) }
            ]}
            rows={cohort}
          />
        </Section>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, alignItems: 'start' }}>
        <Section title="Pendências de conciliação" subtitle={`Itens pendentes: ${formatNumber(reconciliation?.pending?.pendingItems)}`}>
          <SimpleTable
            columns={[
              { key: 'payment_id', label: 'Pagamento' },
              { key: 'reconciliation_status', label: 'Status' },
              { key: 'amount', label: 'Valor', render: (v) => formatCurrency(v) },
              { key: 'created_at', label: 'Criado em', render: (v) => formatDate(v, { dateStyle: 'short', timeStyle: 'short' }) }
            ]}
            rows={reconciliation?.items || []}
          />
        </Section>

        <Section title="Auditoria de webhooks" subtitle="Últimos eventos financeiros recebidos do provedor.">
          <SimpleTable
            columns={[
              { key: 'provider', label: 'Provedor' },
              { key: 'event_type', label: 'Evento' },
              { key: 'status', label: 'Status' },
              { key: 'created_at', label: 'Recebido em', render: (v) => formatDate(v, { dateStyle: 'short', timeStyle: 'short' }) },
              { key: 'retry_count', label: 'Retries', render: (v) => formatNumber(v) }
            ]}
            rows={webhooks}
          />
        </Section>
      </div>
    </div>
  )
}
